import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import ArrowRightGrey from "@vahak/core/dist/icons/ArrowRightGrey.svg";
import CrossIcon from "@vahak/core/dist/standard-icons/cross-icon.svg";
import LocationPinOutlined from "@vahak/core/dist/standard-icons/location-pin-outlined.svg";
import { NoSuccessErrorHandler } from "@vahak/core/dist/constants";
import DotLoader from "@vahak/core-ui/dist/components/DotLoader";
import { getJoinedStringFromArray } from "@vahak/core-ui/dist/methods";

import useHomepageV2Translation from "../hooks/useHomepageV2Translation";
import { getRouteLocationDisplay } from "../load-market-route";
import { openGoogleMapsRoute, LatLng } from "../../../methods/maps";
import { useGetLoadStops, LoadStopInfo } from "../../../services/load/useGetLoadStops";
import Styles from "./DetailedRouteModal.module.scss";

export interface DetailedRouteLoadDetail {
    source_city_name?: string;
    source_state_name?: string;
    source_formatted_address?: string;
    source_latitude?: number;
    source_longitude?: number;
    destination_city_name?: string;
    destination_state_name?: string;
    destination_formatted_address?: string;
    destination_latitude?: number;
    destination_longitude?: number;
}

type RoutePointType = "from" | "stop" | "to";

interface RoutePoint {
    type: RoutePointType;
    address: string;
    latLng?: LatLng;
}

function formatStopBodyAddress(info?: LoadStopInfo) {
    const { description, address } = info || {};
    return getJoinedStringFromArray([description, address], ", ", true);
}

function formatStopHeaderAddress(info?: LoadStopInfo) {
    return info?.address?.trim() || "";
}

function toLatLng(info?: { latitude?: number; longitude?: number }): LatLng | undefined {
    if (info?.latitude == null || info?.longitude == null) {
        return undefined;
    }
    return [info.latitude, info.longitude];
}

function parseStopsToPoints(stops: LoadStopInfo[]): RoutePoint[] {
    if (!stops.length) {
        return [];
    }

    const [src, ...rest] = stops;
    const dest = rest.pop();
    const waypoints = [...rest].sort((a, b) => Number(a.sequence) - Number(b.sequence));

    return [
        { type: "from" as const, address: formatStopBodyAddress(src), latLng: toLatLng(src) },
        ...waypoints.map(
            (stop): RoutePoint => ({
                type: "stop",
                address: formatStopBodyAddress(stop),
                latLng: toLatLng(stop)
            })
        ),
        { type: "to" as const, address: formatStopBodyAddress(dest), latLng: toLatLng(dest) }
    ].filter((point) => point.address || point.latLng);
}

function buildFallbackPoints(loadDetail?: DetailedRouteLoadDetail): RoutePoint[] {
    if (!loadDetail) {
        return [];
    }

    const sourceAddress =
        loadDetail.source_formatted_address ||
        getJoinedStringFromArray([loadDetail.source_city_name, loadDetail.source_state_name], ", ", true);
    const destinationAddress =
        loadDetail.destination_formatted_address ||
        getJoinedStringFromArray([loadDetail.destination_city_name, loadDetail.destination_state_name], ", ", true);

    const points: RoutePoint[] = [];

    if (sourceAddress) {
        points.push({
            type: "from",
            address: sourceAddress,
            latLng: toLatLng({ latitude: loadDetail.source_latitude, longitude: loadDetail.source_longitude })
        });
    }

    if (destinationAddress) {
        points.push({
            type: "to",
            address: destinationAddress,
            latLng: toLatLng({
                latitude: loadDetail.destination_latitude,
                longitude: loadDetail.destination_longitude
            })
        });
    }

    return points;
}

function getRouteChipLabels(stops: LoadStopInfo[], loadDetail?: DetailedRouteLoadDetail): string[] {
    if (stops.length) {
        const [src, ...rest] = stops;
        const dest = rest.pop();
        const waypoints = [...rest].sort((a, b) => Number(a.sequence) - Number(b.sequence));
        const labels: string[] = [];

        const sourceLabel = formatStopHeaderAddress(src) || loadDetail?.source_city_name?.trim() || "";
        if (sourceLabel) {
            labels.push(sourceLabel);
        }

        waypoints.forEach((stop) => {
            const label = formatStopHeaderAddress(stop);
            if (label) {
                labels.push(label);
            }
        });

        const destinationLabel = formatStopHeaderAddress(dest) || loadDetail?.destination_city_name?.trim() || "";
        if (destinationLabel) {
            labels.push(destinationLabel);
        }

        if (labels.length) {
            return labels;
        }
    }

    const { sourceCity, destinationCity } = getRouteLocationDisplay(loadDetail);
    return [sourceCity, destinationCity].filter((label): label is string => Boolean(label));
}

export interface DetailedRouteModalProps {
    isOpen: boolean;
    onClose: () => void;
    loadId: number;
    loadDetail?: DetailedRouteLoadDetail;
}

const DetailedRouteModal = ({ isOpen, onClose, loadId, loadDetail }: DetailedRouteModalProps) => {
    const { t } = useHomepageV2Translation();
    const [isMounted, setIsMounted] = useState(false);

    const response = useGetLoadStops(
        { loadId },
        {
            enabled: isOpen && !!loadId,
            ...NoSuccessErrorHandler
        }
    );

    const stops = response.data?.data?.stops;

    const points = useMemo(() => {
        if (stops?.length) {
            const parsed = parseStopsToPoints(stops);
            if (parsed.length) {
                return parsed;
            }
        }
        return buildFallbackPoints(loadDetail);
    }, [loadDetail, stops]);

    const routeLabels = useMemo(() => getRouteChipLabels(stops || [], loadDetail), [loadDetail, stops]);

    const allLatLng = useMemo(
        () => points.map((point) => point.latLng).filter((coords): coords is LatLng => !!coords),
        [points]
    );

    const isLoading = isOpen && !!loadId && (response.isLoading || response.isFetching) && !points.length;

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    const routeChip = useMemo(
        () =>
            routeLabels.map((label, index) => (
                <React.Fragment key={`${label}-${index}`}>
                    {index > 0 ? (
                        <span className={Styles.routeChipArrow} aria-hidden>
                            <ArrowRightGrey />
                        </span>
                    ) : null}
                    <span className={Styles.routeChipCity}>{label}</span>
                </React.Fragment>
            )),
        [routeLabels]
    );

    if (!isOpen || !isMounted) {
        return null;
    }

    return createPortal(
        <div className={Styles.overlay} role="presentation" onClick={onClose}>
            <div
                className={Styles.shell}
                role="dialog"
                aria-modal="true"
                aria-labelledby="detailed-route-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className={Styles.heroHeader}>
                    <div className={Styles.headerRow}>
                        <h2 className={Styles.title} id="detailed-route-title">
                            {t("homepageV2:detailedRoute.title")}
                        </h2>
                        <button type="button" className={Styles.closeBtn} onClick={onClose} aria-label="Close">
                            <CrossIcon width={16} height={16} />
                        </button>
                    </div>
                    {routeLabels.length > 0 ? <div className={Styles.routeChip}>{routeChip}</div> : null}
                </div>

                <div className={Styles.content}>
                    {isLoading ? (
                        <div className={Styles.loaderWrap}>
                            <DotLoader />
                        </div>
                    ) : points.length === 0 ? (
                        <p className={Styles.emptyState}>{t("homepageV2:detailedRoute.empty")}</p>
                    ) : (
                        <div className={Styles.pointList}>
                            {points.map((point, index) => (
                                <div key={`${point.type}-${index}`} className={Styles.pointRow}>
                                    <div className={Styles.pointMain}>
                                        <span className={Styles.pointLabel}>
                                            {point.type === "from"
                                                ? t("homepageV2:detailedRoute.from")
                                                : point.type === "to"
                                                ? t("homepageV2:detailedRoute.to")
                                                : t("homepageV2:detailedRoute.stop")}
                                        </span>
                                        <p className={Styles.pointAddress}>{point.address || "--"}</p>
                                    </div>
                                    {/* {point.type === "to" && allLatLng.length > 0 ? (
                                        <button
                                            type="button"
                                            className={Styles.mapBtn}
                                            onClick={() => openGoogleMapsRoute(allLatLng)}
                                        >
                                            <LocationPinOutlined className={Styles.mapBtnIcon} aria-hidden />
                                            {t("homepageV2:detailedRoute.showOnMap")}
                                        </button>
                                    ) : null} */}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DetailedRouteModal;
